<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAgentsList } from 'dashboard/composables/useAgentsList';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ContactTaskItem from './ContactTaskItem.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
  conversationId: { type: [String, Number], default: undefined },
});

const { t } = useI18n();
const store = useStore();
const { agentsList } = useAgentsList(false);
const contactGetter = useMapGetter('contacts/getContact');
const uiFlags = useMapGetter('contactTasks/getUIFlags');
const tasksByContact = useMapGetter('contactTasks/getAllTasksByContactId');

const contactId = computed(() => props.contactId);
const contact = computed(() => contactGetter.value(contactId.value) || {});
const companyName = computed(() => contact.value.company?.name);

const isFetching = computed(() => uiFlags.value.isFetching);
const isCreating = computed(() => uiFlags.value.isCreating);

const tasks = computed(() => tasksByContact.value(contactId.value) || []);

const shouldShowCreateModal = ref(false);
const taskDescription = ref('');
const taskDueAt = ref('');
const shouldAssign = ref(false);
const taskAssignee = ref(null);

const resetForm = () => {
  taskDescription.value = '';
  taskDueAt.value = '';
  shouldAssign.value = false;
  taskAssignee.value = null;
};

const openCreateModal = () => {
  resetForm();
  shouldShowCreateModal.value = true;
};

const closeCreateModal = () => {
  shouldShowCreateModal.value = false;
  resetForm();
};

const onSelectAssignee = agent => {
  taskAssignee.value = agent?.id ? agent : null;
};

const onToggleAssign = () => {
  shouldAssign.value = !shouldAssign.value;
  if (!shouldAssign.value) {
    taskAssignee.value = null;
  }
};

const onCreate = async () => {
  if (!taskDescription.value.trim()) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.VALIDATION_ERROR'));
    return;
  }

  try {
    await store.dispatch('contactTasks/create', {
      contactId: contactId.value,
      description: taskDescription.value.trim(),
      due_at: taskDueAt.value
        ? new Date(taskDueAt.value).toISOString()
        : null,
      assignee_id: shouldAssign.value ? taskAssignee.value?.id || null : null,
      conversation_id: props.conversationId || null,
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.CREATE_SUCCESS'));
    closeCreateModal();
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.ERROR_MESSAGE'));
  }
};

const onStatusChange = async (task, status) => {
  try {
    await store.dispatch('contactTasks/update', {
      contactId: contactId.value,
      taskId: task.id,
      status,
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.ERROR_MESSAGE'));
  }
};

const onDelete = async task => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DELETE_CONFIRM'))) {
    return;
  }

  try {
    await store.dispatch('contactTasks/delete', {
      contactId: contactId.value,
      taskId: task.id,
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.ERROR_MESSAGE'));
  }
};

watch(
  contactId,
  newContactId => {
    closeCreateModal();
    if (newContactId) {
      store.dispatch('contactTasks/get', { contactId: newContactId });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div v-if="companyName" class="px-4 pt-3 text-sm text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.COMPANY_LABEL') }}:
      <span class="font-medium text-n-slate-12">{{ companyName }}</span>
    </div>
    <div class="px-4 pt-3 pb-2">
      <NextButton
        ghost
        xs
        icon="i-lucide-plus"
        :label="t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ADD_TASK')"
        :disabled="!contactId || isFetching"
        @click="openCreateModal"
      />
    </div>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="tasks.length"
      class="flex flex-col max-h-[300px] overflow-y-auto"
    >
      <ContactTaskItem
        v-for="task in tasks"
        :key="task.id"
        class="py-3 last-of-type:border-b-0 px-4"
        :task="task"
        @status-change="status => onStatusChange(task, status)"
        @delete="() => onDelete(task)"
      />
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.EMPTY_STATE') }}
    </p>

    <woot-modal
      v-model:show="shouldShowCreateModal"
      :on-close="closeCreateModal"
      :close-on-backdrop-click="false"
      class="!items-start [&>div]:!top-12 [&>div]:sticky"
    >
      <div class="flex w-full flex-col gap-4 px-6 py-6">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.TITLE') }}
        </h3>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DESCRIPTION_LABEL') }}
          </label>
          <textarea
            v-model="taskDescription"
            rows="3"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 resize-none"
            :placeholder="
              t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DESCRIPTION_PLACEHOLDER')
            "
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DUE_AT_LABEL') }}
          </label>
          <input
            v-model="taskDueAt"
            type="datetime-local"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label
            class="flex items-center gap-1.5 text-xs text-n-slate-10 cursor-pointer w-fit"
          >
            <input
              type="checkbox"
              class="h-3 w-3"
              :checked="shouldAssign"
              @change="onToggleAssign"
            />
            {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGN_TOGGLE_LABEL') }}
          </label>
          <div v-if="shouldAssign" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.SIDEBAR.TASKS.ASSIGNEE_LABEL') }}
            </label>
            <MultiselectDropdown
              :options="agentsList"
              :selected-item="taskAssignee || {}"
              :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
              :multiselector-placeholder="
                $t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')
              "
              :no-search-result="
                $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
              "
              :input-placeholder="
                $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
              "
              @select="onSelectAssignee"
            />
          </div>
        </div>
        <div class="flex items-center justify-end gap-3">
          <NextButton
            solid
            blue
            :label="t('CONTACTS_LAYOUT.SIDEBAR.TASKS.SAVE')"
            :is-loading="isCreating"
            :disabled="!taskDescription.trim() || isCreating"
            @click="onCreate"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>
